import * as Australia from "@kisacademics/lps/curriculums/australia";
import { KIS } from "@kisacademics/lps/curriculums/kis";
import type { Curriculum, Course as LpsCourse } from "@kisacademics/lps";

export interface Course {
  code: string;
  name: string;
  curriculum: { id: string; name: string };
}

const SENIOR_CURRICULUMS: Curriculum[] = Object.values(Australia);
const ALL_CURRICULUMS: Curriculum[] = [KIS, ...SENIOR_CURRICULUMS];

const SELECTIVE_ENTRY_CODE = "SELECTIVE-ENTRY";
const UCAT_CODE = "UCAT";

function toCourse(c: LpsCourse): Course {
  return {
    code: c.code,
    name: c.name,
    curriculum: { id: c.curriculum.id, name: c.curriculum.name },
  };
}

function getCurriculumById(id: string): Curriculum | undefined {
  const lower = id.toLowerCase();
  return ALL_CURRICULUMS.find((c) => c.id.toLowerCase() === lower);
}

// KIS courses are tagged by the trailing segment of their code (-PRIMARY / -SECONDARY).
// Mirrors wayfinder's getCoursesByCurriculumId behavior.
function getKisCoursesForLevel(level: "primary" | "secondary"): LpsCourse[] {
  return KIS.courses.filter((course) => {
    const trailing = course.code.split("-").pop()?.toLowerCase();
    return trailing === level;
  });
}

function withUniversalExtras(courses: LpsCourse[]): LpsCourse[] {
  const codes = new Set(courses.map((c) => c.code));
  const extras: LpsCourse[] = [];
  const selective = KIS.courses.find((c) => c.code === SELECTIVE_ENTRY_CODE);
  const ucat = KIS.courses.find((c) => c.code === UCAT_CODE);
  if (selective && !codes.has(SELECTIVE_ENTRY_CODE)) extras.push(selective);
  if (ucat && !codes.has(UCAT_CODE)) extras.push(ucat);
  return [...courses, ...extras];
}

export function getCoursesByCurriculumId(curriculumId: string): Course[] {
  const id = curriculumId.toLowerCase();

  if (id === "primary" || id === "secondary") {
    return withUniversalExtras(getKisCoursesForLevel(id)).map(toCourse);
  }

  const curriculum = getCurriculumById(curriculumId);
  if (!curriculum) return [];
  return withUniversalExtras(curriculum.courses).map(toCourse);
}

export function getCurriculumsByYear(
  year: "1_6" | "7_10" | "11_12",
): { value: string; label: string }[] {
  if (year === "1_6") return [{ value: "primary", label: "Primary School" }];
  if (year === "7_10") return [{ value: "secondary", label: "Secondary School" }];
  if (year === "11_12") {
    return SENIOR_CURRICULUMS.map((c) => ({ value: c.id, label: c.name }));
  }
  return [];
}
